import { DataTableProps } from './props.js'
import { Skeleton } from './Skeleton.js'

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  footer,
  loading = false,
  className = '',
  stackOnMobile = false,
  stackBelow = 'md',
  embedded = false,
  fixedLayout = false,
}: DataTableProps<T>) {
  // Desktop table view
  const tableContent = (
    <>
      <table className={`w-full border-collapse ${fixedLayout ? 'table-fixed' : ''}`}>
        <thead>
          <tr className="border-b border-outline-variant">
            {columns.map((col) => (
              <th
                key={col.key}
                className={[
                  'text-label-md text-on-surface-variant font-medium',
                  'text-start px-4 py-2.5',
                  col.numeric && 'text-end',
                  col.wrap ? 'whitespace-normal break-words' : '',
                  col.width,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-b border-outline-variant last:border-0 h-12">
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-0 h-12 align-middle">
                    <Skeleton lines={1} />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-on-surface-variant">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={rowKey(row)}
                className={[
                  'border-b border-outline-variant last:border-0',
                  'h-12',
                  onRowClick && 'cursor-pointer hover:bg-surface-high interactive',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onRowClick?.(row)}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    onRowClick(row)
                  }
                }}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={[
                      'text-body-md text-on-surface',
                      'px-4 align-middle',
                      col.wrap ? 'py-2 whitespace-normal [overflow-wrap:anywhere]' : 'py-0 h-12 whitespace-nowrap truncate',
                      col.numeric && 'text-end tabular',
                      col.width,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {col.cell(row, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="border-t border-outline-variant bg-surface-low">
              <td colSpan={columns.length} className="px-4 py-2.5">
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </>
  )

  const desktopView = embedded ? (
    <div className="overflow-x-auto">{tableContent}</div>
  ) : (
    <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
      <div className="overflow-x-auto">{tableContent}</div>
    </div>
  )

  // Mobile stacked view — each row as a bordered block with label: value pairs
  const mobileView = stackOnMobile && (
    <div className={embedded ? '' : 'space-y-3'}>
      {loading ? (
        Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className={[
              'space-y-2 p-4',
              !embedded && 'bg-surface-container border border-outline-variant rounded-lg',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {columns
              .filter((col) => !col.hideOnMobile)
              .slice(0, 3)
              .map((col) => (
                <div key={col.key} className="flex items-baseline justify-between">
                  <span className="text-label-md text-on-surface-variant font-medium">
                    {col.header}
                  </span>
                  <Skeleton lines={1} />
                </div>
              ))}
          </div>
        ))
      ) : rows.length === 0 ? (
        <div className="text-center py-8 text-on-surface-variant">{empty}</div>
      ) : (
        rows.map((row, index) => (
          <div
            key={rowKey(row)}
            className={[
              'space-y-2 p-4',
              !embedded && 'bg-surface-container border border-outline-variant rounded-lg',
              onRowClick && 'cursor-pointer hover:bg-surface-high interactive',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onRowClick?.(row)}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                onRowClick(row)
              }
            }}
          >
            {columns
              .filter((col) => !col.hideOnMobile)
              .map((col) => (
                <div key={col.key} className="flex items-baseline justify-between gap-4">
                  <span className="text-label-md text-on-surface-variant font-medium">
                    {col.header}
                  </span>
                  <div
                    className={[
                      'text-body-md text-on-surface',
                      col.numeric && 'text-end tabular',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {col.cell(row, index)}
                  </div>
                </div>
              ))}
          </div>
        ))
      )}
      {footer && (
        <div className={embedded ? 'px-4 py-2.5' : 'bg-surface-low border-t border-outline-variant px-4 py-2.5'}>
          {footer}
        </div>
      )}
    </div>
  )

  return (
    <div className={className}>
      <div className={stackBelow === 'lg' ? 'hidden lg:block' : 'hidden md:block'}>{desktopView}</div>
      {stackOnMobile && <div className={stackBelow === 'lg' ? 'lg:hidden' : 'md:hidden'}>{mobileView}</div>}
      {!stackOnMobile && <div>{desktopView}</div>}
    </div>
  )
}
